import React, { useEffect, useState } from "react";
import SummaryApi from "../../../common"; 

const AllLeave = () => {
  const [leaves, setLeaves] = useState([]);
  const [filteredLeaves, setFilteredLeaves] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLeave, setEditingLeave] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [formData, setFormData] = useState({
    staffId: "",
    startDate: "",
    endDate: "",
    reason: "",
    status: "Pending",
    rejectionReason: "",
  });

  // ✅ Fetch all leaves
  const fetchLeaves = async () => {
    try {
      const res = await fetch(SummaryApi.leave.getAll.url);
      if (!res.ok) throw new Error("Failed to fetch leaves");
      const data = await res.json();
      setLeaves(data);
      setFilteredLeaves(data);
    } catch (error) {
      console.error("Error fetching leaves:", error);
    }
  };

  // ✅ Fetch staff list
  const fetchStaff = async () => {
    try {
      const res = await fetch(SummaryApi.staff.getAll.url);
      if (!res.ok) throw new Error("Failed to fetch staff");
      const data = await res.json();
      setStaffList(data);
    } catch (err) {
      console.error("Error fetching staff:", err);
    }
  };

  useEffect(() => {
    fetchLeaves();
    fetchStaff();
  }, []);

  // ✅ Filter leaves by month
  useEffect(() => {
    if (selectedMonth === "") {
      setFilteredLeaves(leaves);
    } else {
      const filtered = leaves.filter((leave) => {
        const leaveMonth = new Date(leave.startDate).toISOString().slice(0, 7);
        return leaveMonth === selectedMonth;
      });
      setFilteredLeaves(filtered);
    }
  }, [selectedMonth, leaves]);

  // ✅ Handle form input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Handle form submit (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { method, url } = editingLeave
        ? SummaryApi.leave.update(editingLeave._id)
        : SummaryApi.leave.create;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!res.ok) throw new Error("Failed to submit leave");
      await res.json();
      setIsModalOpen(false);
      setEditingLeave(null);
      setFormData({ staffId: "", startDate: "", endDate: "", reason: "", status: "Pending", rejectionReason: "" });
      fetchLeaves();
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  // ✅ Handle edit
  const handleEdit = (leave) => {
    setEditingLeave(leave);
    setFormData({
      staffId: leave.staffId?._id || "",
      startDate: leave.startDate?.slice(0, 10) || "",
      endDate: leave.endDate?.slice(0, 10) || "",
      reason: leave.reason || "",
      status: leave.status || "Pending",
      rejectionReason: leave.rejectionReason || "",
    });
    setIsModalOpen(true);
  };

  // ✅ Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this leave?")) return;
    try {
      const { method, url } = SummaryApi.leave.delete(id);
      const res = await fetch(url, { method });
      if (!res.ok) throw new Error("Failed to delete leave");
      fetchLeaves();
    } catch (error) {
      console.error("Error deleting leave:", error);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Leave Applications</h2>
        <button
          onClick={() => {
            setEditingLeave(null);
            setFormData({ staffId: "", startDate: "", endDate: "", reason: "", status: "Pending", rejectionReason: "" });
            setIsModalOpen(true);
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md shadow"
        >
          + Create Leave
        </button>
      </div>

      {/* Month Filter */}
      <div className="bg-white shadow rounded-lg p-4 mb-4">
        <div className="flex items-center gap-4">
          <label className="text-gray-700 font-medium">Filter by Month:</label>
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {selectedMonth && (
            <button
              onClick={() => setSelectedMonth("")}
              className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-sm"
            >
              Clear Filter
            </button>
          )}
          <span className="text-gray-600 text-sm ml-auto">
            Showing {filteredLeaves.length} of {leaves.length} applications
          </span>
        </div>
      </div>

      {/* Leave Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full text-sm text-left text-gray-600">
          <thead className="bg-gray-100 text-gray-800">
            <tr>
              <th className="py-3 px-4">Staff Name</th>
              <th className="py-3 px-4">Applied Date</th>
              <th className="py-3 px-4">Start Date</th>
              <th className="py-3 px-4">End Date</th>
              <th className="py-3 px-4">Days</th>
              <th className="py-3 px-4">Reason</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredLeaves.length > 0 ? (
              filteredLeaves.map((leave) => (
                <tr
                  key={leave._id}
                  className="border-b hover:bg-gray-50 transition-colors"
                >
                  <td className="py-3 px-4">{leave.staffId?.name || "N/A"}</td>
                  <td className="py-3 px-4">
                    {new Date(leave.appliedDate).toLocaleDateString("en-GB")}
                  </td>
                  <td className="py-3 px-4">
                    {new Date(leave.startDate).toLocaleDateString("en-GB")}
                  </td>
                  <td className="py-3 px-4">
                    {new Date(leave.endDate).toLocaleDateString("en-GB")}
                  </td>
                  <td className="py-3 px-4">{leave.daysCount}</td>
                  <td className="py-3 px-4">{leave.reason}</td>
                  <td className="py-3 px-4 font-medium">
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        leave.status === "Approved"
                          ? "bg-green-100 text-green-700"
                          : leave.status === "Rejected"
                          ? "bg-red-100 text-red-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {leave.status}
                    </span>
                  </td>
                  <td className="p-2 flex justify-center gap-2">
                    <button
                      onClick={() => handleEdit(leave)}
                      className="bg-yellow-500 text-white px-2 py-1 rounded-md hover:bg-yellow-600"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(leave._id)}
                      className="bg-red-500 text-white px-2 py-1 rounded-md hover:bg-red-600"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center py-6 text-gray-500 italic">
                  {selectedMonth ? "No leave applications found for selected month" : "No leave applications found"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white w-full max-w-lg p-6 rounded-lg shadow-lg">
            <h3 className="text-lg font-semibold mb-4">
              {editingLeave ? "Update Leave" : "Create Leave"}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* ✅ Show Staff dropdown only when creating */}
              {!editingLeave && (
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">
                    Select Staff
                  </label>
                  <select
                    name="staffId"
                    value={formData.staffId}
                    onChange={handleChange}
                    className="w-full p-2 border rounded"
                    required
                  >
                    <option value="">-- Select Staff --</option>
                    {staffList.map((staff) => (
                      <option key={staff._id} value={staff._id}>
                        {staff.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Start Date */}
              <div>
                <label className="block text-gray-700 mb-1 font-medium">
                  Start Date
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>

              {/* End Date */}
              <div>
                <label className="block text-gray-700 mb-1 font-medium">
                  End Date
                </label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>

              {/* Reason */}
              <div>
                <label className="block text-gray-700 mb-1 font-medium">
                  Reason
                </label>
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  placeholder="Enter reason for leave"
                  className="w-full p-2 border rounded"
                  required
                />
              </div>

              {/* ✅ Status dropdown only when editing */}
              {editingLeave && (
                <>
                  <div>
                    <label className="block text-gray-700 mb-1 font-medium">
                      Status
                    </label>
                    <select
                      name="status"
                      value={formData.status || "Pending"}
                      onChange={handleChange}
                      className="w-full p-2 border rounded"
                    >
                      <option value="Pending">Pending</option>
                      <option value="Approved">Approved</option>
                      <option value="Rejected">Rejected</option>
                    </select>
                  </div>

                  {/* ✅ Rejection reason field (only when status = Rejected) */}
                  {formData.status === "Rejected" && (
                    <div>
                      <label className="block text-gray-700 mb-1 font-medium">
                        Rejection Reason
                      </label>
                      <textarea
                        name="rejectionReason"
                        value={formData.rejectionReason || ""}
                        onChange={handleChange}
                        placeholder="Enter reason for rejection"
                        className="w-full p-2 border rounded"
                      />
                    </div>
                  )}
                </>
              )}

              <div className="flex justify-end gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded"
                >
                  {editingLeave ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllLeave;